/**
 * 67.52 (the owner, Oct 13: *"the hmg is slightly too deeply embedded into the terrain when built, as is fob … just 20% too
 * deep"*; *"when built on rock they don't bury into the terrain but sit on top of it (with maybe a little blend just to look
 * good)"*): where a person-scale build stands. A structure stood on the coarse ground (core/hits.ts, its world position), and
 * the drawing flattened the fine relief under it to that ground (rendering/fineFlatten.ts); but the fine ground everyone
 * else stands on is lifted over the coarse a median 0.6 people (0.5–0.8 round a footprint) — the HMG's ring (0.5 people
 * tall) and the FOB's crates (0.72) stood in a pit, in the sim as drawn. Now a build's base stands on its spot's fine ground,
 * set once when it is placed (`StructureState.baseLift`):
 * - on soil, mud, sand, snow, ash or salt: the mean of the fine ground round its footprint, bedded BUILD_SEAT.beddingPeople;
 * - on rock or ice (which refuse a dig too, core/dig.ts): the highest of it, less BUILD_SEAT.rockBlendPeople — on top.
 * Pure; read once at placement, so the ground's later changes (a dig, a crater) leave it where it was built.
 */
import type { GameState, StructureState, StructureType, Vec3 } from '../types';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { personSurfaceRadius } from './fineTerrain';
import { getSurfaceRadius } from './surface';
import { groundMaterial } from './groundMaterial';
import { personScaleStructureHalfWidth } from './surfaceWalk';
import { dcos, dsin } from './dmath';

/** 67.52: a build's bedding on soft ground and its blend on rock (people). */
export const BUILD_SEAT = { beddingPeople: 0.1, rockBlendPeople: 0.03, ringPoints: 12 } as const;

/** The person-scale build kinds that stand on the fine ground (vehicles drive on it already). */
export const SEATED_BUILDS: ReadonlySet<StructureType> = new Set<StructureType>(['fob', 'hmg', 'relay', 'beacon', 'supply', 'emplacement', 'tower', 'landingPad', 'shield', 'ecotourism', 'antenna', 'house', 'motorPool', 'pointDefence', 'helipad']);   // (209's footing follow-up: the buildings on their plinths; the base planner: 69.9's mounts level, not on the slope)
export { PAD_DECK_HALF_PEOPLE } from '../constants/action';   // (209: the pad's deck — its half, constants/action.ts)
import { footingOf } from './stands';

/**
 * The lift (world units over the coarse ground at `dir`) a build of `type` takes there, or undefined where it takes none:
 * not a seated build, not person-scale on this clock, or no fine ground (classic).
 */
export function buildBaseLift(state: Pick<GameState, 'config' | 'planets' | 'turnNumber'>, type: StructureType, planetIndex: number, dir: Vec3, seconds = 0, builder?: number): number | undefined {
  if (!SEATED_BUILDS.has(type)) return undefined;
  // 209's footing follow-up: a kind with a walkable top — the pad's deck, a building's plinth (by its builder) — is seated on
  // the highest ground under its footprint, as the pad is drawn (rendering/groundSeat.ts), so no ground stands through it
  const foot = footingOf({ type, builder }, state.config);
  const pad = !!foot;
  const ps = personSetOf(state);
  const half = foot ? Math.max(foot.halfXPeople, foot.halfZPeople) * PERSON_BODY_HEIGHT * ps.k : personScaleStructureHalfWidth({ type }, state.config);
  const planet = state.planets[planetIndex];
  if (half === null || !planet || !(ps.fineAmplitude > 0)) return undefined;
  const person = PERSON_BODY_HEIGHT * ps.k;
  const l = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
  const n = { x: dir.x / l, y: dir.y / l, z: dir.z / l };
  const a = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const e1r = { x: n.y * a.z - n.z * a.y, y: n.z * a.x - n.x * a.z, z: n.x * a.y - n.y * a.x };
  const e1l = Math.sqrt(e1r.x * e1r.x + e1r.y * e1r.y + e1r.z * e1r.z);
  const e1 = { x: e1r.x / e1l, y: e1r.y / e1l, z: e1r.z / e1l };
  const e2 = { x: n.y * e1.z - n.z * e1.y, y: n.z * e1.x - n.x * e1.z, z: n.x * e1.y - n.y * e1.x };
  const t = half / planet.radius, k = BUILD_SEAT.ringPoints;
  let sum = 0, hi = -Infinity;
  for (let i = 0; i < k; i++) {
    const c = dcos((2 * Math.PI * i) / k) * t, s = dsin((2 * Math.PI * i) / k) * t;
    const r = personSurfaceRadius(planet, { x: n.x + e1.x * c + e2.x * s, y: n.y + e1.y * c + e2.y * s, z: n.z + e1.z * c + e2.z * s }, ps);
    sum += r; if (r > hi) hi = r;
  }
  if (pad) {
    // (the deck: on the highest ground under it — the centre and a ring at half its reach too, as the rim's ring alone may miss a crown)
    hi = Math.max(hi, personSurfaceRadius(planet, n, ps));
    for (let i = 0; i < k; i++) {
      const c = dcos((2 * Math.PI * (i + 0.5)) / k) * t * 0.5, s = dsin((2 * Math.PI * (i + 0.5)) / k) * t * 0.5;
      hi = Math.max(hi, personSurfaceRadius(planet, { x: n.x + e1.x * c + e2.x * s, y: n.y + e1.y * c + e2.y * s, z: n.z + e1.z * c + e2.z * s }, ps));
    }
    return Math.max(0, hi - getSurfaceRadius(planet, n));
  }
  const mat = groundMaterial(state, planetIndex, n, seconds);
  const hard = mat === 'rock' || mat === 'ice';
  const top = hard ? hi - BUILD_SEAT.rockBlendPeople * person : sum / k - BUILD_SEAT.beddingPeople * person;
  return Math.max(0, top - getSurfaceRadius(planet, n));   // (never under the coarse ground: no deeper than before)
}

/** The structure seated where it stands (its `baseLift` set; unchanged where it takes none or has one). */
export function seatBuild(state: Pick<GameState, 'config' | 'planets' | 'turnNumber'>, s: StructureState, seconds = 0): StructureState {
  if (s.baseLift !== undefined) return s;
  const lift = buildBaseLift(state, s.type, s.planetIndex, s.surfaceDir, seconds, s.builder);
  return lift === undefined ? s : { ...s, baseLift: lift };
}

/** Every structure of the state seated (a setup's placement, after it has settled them). The same state when none moves. */
export function seatBuilds(state: GameState, seconds = 0): GameState {
  let changed = false;
  const structures = state.structures.map(s => { const t = seatBuild(state, s, seconds); if (t !== s) changed = true; return t; });
  return changed ? { ...state, structures } : state;
}

/** A structure's base radius on its planet: the coarse ground at its centre plus its lift. */
export function structureBaseRadius(planet: Parameters<typeof getSurfaceRadius>[0], s: Pick<StructureState, 'surfaceDir' | 'baseLift'>): number {
  return getSurfaceRadius(planet, s.surfaceDir) + (s.baseLift ?? 0);
}
